import './QuestionWithAnswers.scss';
import { useTranslator } from '@Localization';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect } from 'react';
import { IAnswerModel, IQuestionModel, QuestionType } from '../../question.model';
import { AnswerDropDown } from './AnswerDropDown';
import { AnswerWithCheckBox } from './AnswerWithCheckBox';
import { AnswerWithRadioButton } from './AnswerWithRadioButton';
import { NumericAnswer } from './NumberAnswer';
import { TextAnswer } from './TextAnswer';

interface IProps {
  question: IQuestionModel;
  disabled: boolean;
  isAttribute: boolean;
  onAnswered(questionId: string, answer: string): void;
}

export const QuestionWithAnswers = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();

  useEffect(() => {
    setTimeout(() => {
      const elements = document.getElementsByClassName('text-input-answer-attribute');
      if (elements.length > 0 && document.activeElement !== elements[0]) {
        let element = elements[0];
        if (element.tagName !== 'INPUT' && element.tagName !== 'TEXTAREA') {
          element = elements[0].getElementsByTagName('input')[0] || elements[0].getElementsByTagName('textarea')[0];
        }
        (element as HTMLInputElement).focus();
      }
    }, 0);
  }, []);

  const onAnswered = (answer: string): void => {
    props.onAnswered(props.question.id, answer);
  };

  const renderAnswer = (answer: IAnswerModel): ReactElement | null => {
    const { question } = props;

    switch (question.type) {
      case QuestionType.TRUE_FALSE:
      case QuestionType.ALTERNATIVE:
      case QuestionType.STATUS_GOOD_BAD:
      case QuestionType.SET_TRAIN_TEST:
        return (
          <AnswerWithRadioButton
            disabled={props.disabled}
            name={question.id}
            key={answer.id}
            answer={answer}
            onAnswered={onAnswered}
          />
        );
      case QuestionType.MULTISELECT:
        return (
          <AnswerWithCheckBox
            disabled={props.disabled}
            name={question.id}
            key={answer.id}
            answer={answer}
            onAnswered={onAnswered}
          />
        );
      case QuestionType.OPEN:
      case QuestionType.NUMBER:
      default:
        throw new Error(`Type not implemented: ${question.type}`);
    }
  };

  const renderFeedbacks = (): ReactElement[] => {
    return props.question.status.errorCodes.map((feedback, i) => {
      return <li key={i}>{t(feedback)}</li>;
    });
  };

  const { question } = props;
  const hasErrors = question.status.isValid === false;

  return (
    <div
      className={classNames('question-list-single-item', { invalid: hasErrors })}
      key={question.id}
    >
      <div
        className='question-text'
        title={question.text}
      >
        <span>{question.text}</span> <span className='required'>{question.isRequired ? '*' : ''}</span>
      </div>
      {question.type === QuestionType.ALTERNATIVE && question.answers.length > 9 ? (
        <AnswerDropDown
          disabled={props.disabled}
          answers={question.answers}
          onAnswered={onAnswered}
        />
      ) : (
        question.answers.length > 0 && <div className='answer-list'>{question.answers.map(renderAnswer)}</div>
      )}
      {question.type === QuestionType.OPEN && (
        <TextAnswer
          disabled={props.disabled}
          className={props.isAttribute ? 'text-input-answer-attribute' : ''}
          answer={question.answer || ''}
          onAnswered={onAnswered}
        />
      )}
      {question.type === QuestionType.NUMBER && (
        <NumericAnswer
          disabled={props.disabled}
          className={props.isAttribute ? 'text-input-answer-attribute' : ''}
          answer={question.answer || ''}
          onAnswered={onAnswered}
        />
      )}
      {hasErrors && <ul className='feedback'>{renderFeedbacks()}</ul>}
    </div>
  );
});
